import React from "react";
import Backend from "../../backend/Backend"
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
} from "@mui/material";
import {visuallyHidden} from '@mui/utils';
import PropTypes from 'prop-types';
import {useNavigate} from "react-router-dom";

function createData(employee, employeeID, courseCount) {
    return {employee, employeeID, courseCount};
}

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    {
        id: 'employee',
        numeric: false,
        disablePadding: false,
        label: 'Mitarbeiter',
    },
    {
        id: 'courseCount',
        numeric: false,
        disablePadding: false,
        label: 'Anzahl der Kurse',
    },
]

function EnhancedTableHead(props) {
    const {order, orderBy, onRequestSort} =
        props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

export default function OverviewTable() {

    React.useEffect(() => {
        getAllEmployees();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [rows, setRows] = React.useState([]);
    const [originalRows, setOriginalRows] = React.useState([]);
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('employee');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const navigate = useNavigate();


    function getAllEmployees() {
        Backend.getData('/api/TAB_MITARBEITER/')
            .then(generateRows);
    }

    async function loadDataFromDatabase(rawEmployeeData) {
        let tempRows = [];
        for (const employee of rawEmployeeData) {
            let courseCount = 0;
            await Backend.getData(`/api/TAB_KURSVERWALTUNG?KV_Mitarbeiter=${employee['M_Pers-Nr']}&fields=KV_ID`)
                .then(courses => {
                    courseCount = courses.length;
                    tempRows.push(createData(employee.M_Name + ", " + employee.M_Vorname, employee['M_Pers-Nr'], courseCount));
                });
        }
        return tempRows;
    }

    function generateRows(rawEmployeeData) {
        loadDataFromDatabase(rawEmployeeData).then(data => {
            setOriginalRows(data)
            setRows(data);
        });
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const toEmployees = (employee, employeeID) => {
        navigate("/employees", {state: {id: employeeID, name: employee}});
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (event) => {
        if (event.target.value.length !== 0) {
            const filteredRows = originalRows.filter((row) => {
                return row.employee.toLowerCase().includes(event.target.value.toLowerCase());
            });
            setRows(filteredRows);
        } else {
            setRows(originalRows);
        }
    }

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    return (
        <Box sx={{width: '98%'}} style={{
            position: 'absolute', left: '50%',
            transform: 'translate(-50%)'
        }}>
            <Paper sx={{width: '100%', mb: 2}}>
                <TextField
                    variant={"outlined"}
                    label={"Suche..."}
                    sx={{marginLeft: '35%', marginRight: '35%', marginTop:'50px', width: '30%'}}
                    onChange={handleSearch}
                />
                <TableContainer>
                    <Table
                        sx={{minWidth: 750}}
                        width={'80%'}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                    >
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                        />
                        <TableBody>
                            {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
                            {stableSort(rows, getComparator(order, orderBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {
                                    const labelId = `enhanced-table-checkbox-${index}`;

                                    return (
                                        <TableRow
                                            hover
                                            tabIndex={-1}
                                            key={index}
                                        >
                                            <TableCell
                                                component="th"
                                                id={labelId}
                                                scope="row"
                                                padding="none"
                                                style={{cursor: "pointer"}}
                                                onClick={() => toEmployees(row.employee, row.employeeID)}
                                            >
                                                {row.employee}
                                            </TableCell>
                                            <TableCell align="left">{row.courseCount}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            {emptyRows > 0 && (
                                <TableRow
                                    style={{
                                        height: (53) * emptyRows,
                                    }}
                                >
                                    <TableCell colSpan={6}/>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

        </Box>
    );
}

